import jStat from 'jstat';

import map from 'lodash/map';
import get from 'lodash/get';
import times from 'lodash/times';
import random from 'lodash/random';

import { change } from 'redux-form';
import { createSelector } from 'reselect';

import { round4 } from 'utilities/math';

import * as store from 'store';

import { BOOTSTRAP_CIS_FORM } from 'store/form';
import {
  getFilteredParametersValuesNotEmpty,
  getFilteredParameterValuesNotEmpty,
  getSelectedParameters,
  getSelectedParameterType
} from 'store/dataset';

import {
  getAlpha,
  getTailType,

  getPrimaryParameterName as getNewPrimaryParameterName,
  getSecondaryParameterName as getNewSecondaryParameterName
} from 'store/form/bootstrapCIs/selectors';

import { setPrimaryParameter } from 'store/scenes/bootstrapCIs/primaryParameter/actions';
import { setSecondaryParameter } from 'store/scenes/bootstrapCIs/secondaryParameter/actions';

import {
  getPrimaryParameter as getOldPrimaryParameter
} from 'store/scenes/bootstrapCIs/primaryParameter/selectors';

import {
  getSecondaryParameter as getOldSecondaryParameter
} from 'store/scenes/bootstrapCIs/secondaryParameter/selectors';

import { setTestSelection } from './testSelection';
import { setPrimaryParameterLevel } from './primaryParameterLevel';

import {
  setSamples,
  appendSamples,
  getSamplesAmount,

  getSamplesMean,
  getSamplesStdev
} from './samples';

import * as types from './types';

export const setTestSelectionType = parameterName => (dispatch, getState) => {
  const state = getState();

  const newPrimaryParameterType = getSelectedParameterType(state, parameterName);
  const previousPrimaryParameterType = store.form.bootstrapCIs.getPrimaryParameterType(state);
  const secondaryParameterSelected = store.form.bootstrapCIs.isSecondaryParameterSelected(state);

  if (newPrimaryParameterType !== previousPrimaryParameterType || secondaryParameterSelected) {
    dispatch(change(BOOTSTRAP_CIS_FORM, 'testSelection',
      newPrimaryParameterType === 'numerical'
        ? 'mean'
        : 'proportion'
    ));
  }
};

const setCutoffs = () => (dispatch, getState) => {
  const state = getState();

  const mean = getSamplesMean(state);
  const stdev = getSamplesStdev(state);

  const tailType = getTailType(state);

  const alpha = tailType === 'both'
    ? getAlpha(state) / 2
    : getAlpha(state);

  const cutoffs = {
    leftCutoff: round4(jStat.normal.inv(alpha, mean, stdev)),
    rightCutoff: round4(jStat.normal.inv(1 - alpha, mean, stdev))
  };

  dispatch(
    map(cutoffs, (cutoffValue, cutoffName) => (
      change(
        BOOTSTRAP_CIS_FORM,
        cutoffName,
        cutoffValue
      )
    ))
  );
};

const makeNewParameterSelector = parameterNameSelector => (
  createSelector(getSelectedParameters, parameterNameSelector,
    (selectedParameters, parameterName) => {
      const type = get(selectedParameters[parameterName], 'targetType');

      return { name: parameterName, type };
    }
  )
);

const getNewPrimaryParameter = makeNewParameterSelector(getNewPrimaryParameterName);
const getNewSecondaryParameter = makeNewParameterSelector(getNewSecondaryParameterName);

export const bootstrapSamples = () => (dispatch, getState) => {
  const state = getState();

  const {
    getTestSelection,
    getNumberOfSamples,
    getPrimaryParameterLevel,
    isSecondaryParameterSelected
  } = store.form.bootstrapCIs;

  const newPrimaryParameter = getNewPrimaryParameter(state);
  const newSecondaryParameter = getNewSecondaryParameter(state);
  const secondaryParameterSelected = isSecondaryParameterSelected(state);

  if (!secondaryParameterSelected) {
    newPrimaryParameter.values = getFilteredParameterValuesNotEmpty(state, newPrimaryParameter.name);
    newSecondaryParameter.values = [];
  } else {
    const { firstParsedParameterValues, secondParsedParameterValues } = getFilteredParametersValuesNotEmpty(state, newPrimaryParameter.name, newSecondaryParameter.name);

    newPrimaryParameter.values = firstParsedParameterValues;
    newSecondaryParameter.values = secondParsedParameterValues;
  }

  const oldPrimaryParameter = getOldPrimaryParameter(state);
  const oldSecondaryParameter = getOldSecondaryParameter(state);

  const testSelection = getTestSelection(state);
  const numberOfSamples = getNumberOfSamples(state);
  const primaryParameterLevel = getPrimaryParameterLevel(state);

  const samples = times(numberOfSamples, () => {
    const indexes = times(newPrimaryParameter.values.length,
      () => random(newPrimaryParameter.values.length - 1)
    );

    return indexes.map(index => {
      if (secondaryParameterSelected) {
        return [
          newPrimaryParameter.values[index],
          newSecondaryParameter.values[index]
        ];
      }

      return newPrimaryParameter.values[index];
    });
  });

  const samePrimaryParameter = newPrimaryParameter === oldPrimaryParameter;
  const sameSecondaryParameter = newSecondaryParameter === oldSecondaryParameter;

  dispatch([
    ...(!samePrimaryParameter ? [setPrimaryParameter(newPrimaryParameter)] : []),
    ...(!sameSecondaryParameter ? [setSecondaryParameter(newSecondaryParameter)] : []),

    setTestSelection(testSelection),
    setPrimaryParameterLevel(primaryParameterLevel),

    (samePrimaryParameter && sameSecondaryParameter)
      ? appendSamples(samples)
      : setSamples(samples)
  ]);

  dispatch(setCutoffs());

  const minValue = 1;
  const maxValue = 4500 - getSamplesAmount(state) - numberOfSamples;

  if (numberOfSamples > maxValue) {
    dispatch({
      type: '@@redux-form/UPDATE_SYNC_ERRORS',

      meta: {
        form: 'BOOTSTRAP_CIS_FORM'
      },

      payload: {
        syncErrors: {
          numberOfSamples: `
            Should be an integer greater than or equal ${minValue} and less than or
            equal ${maxValue}! Otherwise an out-of-memory exception can be raised!
          `
        }
      }
    });
  }
};

export const resetBootstrapCIs = () => ({
  type: types.RESET_BOOTSTRAP_CIS
});
